import { Component, Inject } from '@angular/core';
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatchStartErrorDialogComponent } from '../match-start-error-dialog/match-start-error-dialog.component';

export interface MatchStartDialogData {
  currentMatchRunId?: string;
  isFirstMatchRun?: boolean;
  pairsStatistics?: {
    totalPairs: number;
    activeConfirmedPairs: number;
    enisSyncNeeded: number;
    activePairs: number;
    inactivePairs: number;
  };
  participantWarnings?: {
    canStartMatchrun: boolean;
    message: string;
    participants: Array<{ id: string; enisNumber: string; type: 'donor' | 'recipient'; warnings: string[] }>;
  };
}

export interface MatchStartDialogResult {
  confirmed: boolean;
  comment?: string;
  isMajorMatchrun?: boolean;
}

@Component({
  selector: 'app-match-start-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, FormsModule, MatIconModule],
  template: `
    <h2 mat-dialog-title>Start New Matchrun</h2>
    <mat-dialog-content>
      <!-- Show warning if participant warnings exist -->
      <div *ngIf="data?.participantWarnings && !data?.participantWarnings?.canStartMatchrun" class="warning-banner">
        <div class="warning-header">
          <mat-icon class="warning-icon">warning</mat-icon>
          <h3>Cannot Start Matchrun</h3>
        </div>
        <p class="warning-message">{{ data?.participantWarnings?.message }}</p>
        <div class="warning-summary" *ngIf="hasParticipants()">
          <p><strong>{{ getParticipantCount() }} participant(s)</strong> with issues need to be resolved before starting a matchrun.</p>
          <button mat-button color="warn" (click)="showWarningDetails()">
            View {{ getParticipantCount() }} Participant{{ getParticipantCount() > 1 ? 's' : '' }} with Issues
          </button>
        </div>
      </div>

      <!-- Show normal content if no warnings -->
      <ng-container *ngIf="!data?.participantWarnings || data?.participantWarnings?.canStartMatchrun">
        <p>
          You are about to start a new matchrun. This will process all active pairs
          and generate results based on the current dataset.
        </p>

        <p *ngIf="data?.pairsStatistics" class="match-description">
          {{ data.pairsStatistics?.totalPairs }} total pairs. With {{ data.pairsStatistics?.activeConfirmedPairs }} participating,
          enis sync needed: {{ data.pairsStatistics?.enisSyncNeeded }}, {{ data.pairsStatistics?.activePairs }} active
          and {{ data.pairsStatistics?.inactivePairs }} inactive.
        </p>

      </ng-container>

      <mat-form-field *ngIf="!data?.isFirstMatchRun" appearance="outline" class="full-width">
        <mat-label>{{ data?.currentMatchRunId ? 'Comment for current matchrun (optional)' : 'Comment for latest matchrun (optional)' }}</mat-label>
        <textarea
          matInput
          [(ngModel)]="comment"
          rows="4"
          placeholder="Add any notes about the matchrun (e.g., decisions made, crossmatches added, etc.)..."></textarea>
        <mat-hint>{{ data?.currentMatchRunId ? 'This comment will be saved to the matchrun you are currently viewing' : 'This comment will be saved to the latest matchrun before starting the new one' }}</mat-hint>
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">CANCEL</button>
      <button
        mat-raised-button
        color="primary"
        (click)="onConfirm()"
        [disabled]="data?.participantWarnings && !data?.participantWarnings?.canStartMatchrun">
        START MATCHRUN
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content {
      max-width: 600px;
      min-width: 500px;
      padding-top: 16px;
    }
    mat-dialog-content p {
      margin-bottom: 24px;
      color: rgba(0, 0, 0, 0.6);
    }
    .full-width {
      width: 100%;
      margin-bottom: 16px;
    }
    mat-dialog-actions {
      padding: 16px 24px;
    }
    .warning-banner {
      background-color: #fff3e0;
      border-left: 4px solid #ff9800;
      padding: 16px;
      margin-bottom: 20px;
      border-radius: 4px;
    }
    .warning-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
    }
    .warning-icon {
      color: #ff9800;
      font-size: 28px;
      width: 28px;
      height: 28px;
    }
    .warning-header h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #e65100;
    }
    .warning-message {
      margin: 0 0 12px 40px;
      font-size: 14px;
      line-height: 1.5;
      color: #bf360c;
    }
    .warning-summary {
      margin-left: 40px;
    }
    .warning-summary p {
      margin: 0 0 8px 0;
      font-size: 14px;
      color: #d84315;
    }
    .warning-summary button {
      font-size: 14px;
      font-weight: 500;
    }
  `]
})
export class MatchStartDialogComponent {
  comment: string = '';

  constructor(
    public dialogRef: MatDialogRef<MatchStartDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MatchStartDialogData,
    private dialog: MatDialog
  ) {}

  hasParticipants(): boolean {
    return !!(this.data?.participantWarnings?.participants && this.data?.participantWarnings?.participants?.length > 0);
  }

  getParticipantCount(): number {
    return this.data?.participantWarnings?.participants?.length || 0;
  }

  showWarningDetails(): void {
    if (this.data?.participantWarnings && this.data.participantWarnings.participants) {
      this.dialog.open(MatchStartErrorDialogComponent, {
        width: '700px',
        data: {
          title: 'Cannot Start Matchrun',
          message: this.data.participantWarnings.message,
          isParticipantWarningError: true,
          participants: this.data.participantWarnings.participants
        }
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close({ confirmed: false });
  }

  onConfirm(): void {
    this.dialogRef.close({
      confirmed: true,
      comment: this.comment.trim() || undefined,
      isMajorMatchrun: false
    });
  }
}
